import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { PO_STATUS_META, CONDITIONS, CONDITION_META, formatDuration } from "@/lib/inventory";
import { beep } from "@/lib/sfx";
import POPhotos from "@/components/POPhotos";
import {
  ScanLine, PackageCheck, X, MapPin, Undo2, CheckCircle2, AlertTriangle, Loader2,
  Boxes, ChevronRight, Plus, Minus, ArrowLeft, Users, Clock, Activity, Shuffle, Keyboard, CheckSquare, Square,
} from "lucide-react";

const LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_e93c448b-a938-4bcf-95ba-463bd62fed43/artifacts/2d6ua9tk_Logo_Wide_White%20%283%29.png";
const MODES = [
  { v: "product", l: "Product-first", hint: "Scan items; location auto/prompt" },
  { v: "bulk", l: "Bulk", hint: "Set a qty, scan once to add many" },
];
// Canonical bin code (mirrors backend _normalize_location): 'gc1' / 'GC-1' -> 'G-C1'.
const normBin = (s) => { const c = String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, ""); return c.length >= 2 ? c[0] + "-" + c.slice(1) : c; };

export default function ReceivingInventoryStation() {
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const [params, setParams] = useSearchParams();
  const [session, setSession] = useState(null);
  const [data, setData] = useState(null); // full session payload
  const [pickerPOs, setPickerPOs] = useState(null);
  const [mode, setMode] = useState("product");
  const [scan, setScan] = useState("");
  const [kbd, setKbd] = useState(() => localStorage.getItem("bh-rcv-kbd") === "1"); // per-device soft-keyboard preference
  const [skipVerify, setSkipVerify] = useState(() => localStorage.getItem("bh-rcv-skipverify") === "1"); // bypass the confirm-bin step
  const [loc, setLoc] = useState("");
  const [qty, setQty] = useState(1);
  const [condition, setCondition] = useState("good");
  const [overrideLoc, setOverrideLoc] = useState("");
  const [showOverride, setShowOverride] = useState(false);
  const [pendingBarcode, setPendingBarcode] = useState(null);
  const [recommend, setRecommend] = useState(null); // {code, existing:[{location_code, available_qty}], product}
  const [assignMode, setAssignMode] = useState(false); // product scanned but no known bin — scan a bin to assign
  const [pendingLine, setPendingLine] = useState(null); // {received_qty, expected_qty, product_title, sku} of the item being placed
  const [editBin, setEditBin] = useState(null); // {lineId, from} inline bin editor
  const [binVal, setBinVal] = useState("");
  const [mismatch, setMismatch] = useState(null); // {code, barcode} — scanned a bin that isn't where the SKU lives
  const [fx, setFx] = useState(null); // 'ok' | 'err' — transient visual feedback
  const [flash, setFlash] = useState(null); // {type, text}
  const [lastPlacement, setLastPlacement] = useState(null); // {location_code, sku, product_title, quantity, condition}
  const [unexpected, setUnexpected] = useState(null); // barcode
  const [busy, setBusy] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [result, setResult] = useState(null);
  const scanRef = useRef(null);
  const locRef = useRef(null);
  const autoTimerRef = useRef(null);
  const locTimerRef = useRef(null);
  const clearTimerRef = useRef(null); // auto-clears the placement callout ~3s after a successful scan
  const focusLoc = () => setTimeout(() => locRef.current?.focus(), 40);
  // Auto-submit after a hardware scanner finishes streaming (scanner mode only).
  const armAuto = (ref, value, fn) => {
    if (kbd) return; // manual typing mode → wait for Enter/button
    if (ref.current) clearTimeout(ref.current);
    ref.current = setTimeout(() => { if ((value || "").trim()) fn(value); }, 140);
  };

  const focusScan = () => setTimeout(() => scanRef.current?.focus(), 30);

  const refresh = useCallback(async (sid) => {
    const { data } = await api.get(`/inventory/receiving/session/${sid}`);
    setData(data);
  }, []);

  const startForPo = useCallback(async (poId) => {
    try {
      const { data: s } = await api.post("/inventory/receiving/start", { po_id: poId, mode });
      setSession(s);
      // Pin ?po= so a page reload on the iPad resumes this session instead of the picker.
      setParams({ po: poId }, { replace: true });
      await refresh(s.id);
      focusScan();
    } catch (e) {
      toast.error(formatApiError(e));
      // Stale/closed PO in the URL — clear it and drop back to the picker.
      setParams({}, { replace: true });
      try {
        const { data } = await api.get("/inventory/pos");
        setPickerPOs((data.pos || []).filter((p) => ["approved", "location_planning", "ready_to_receive", "receiving"].includes(p.status)));
      } catch { setPickerPOs([]); }
    }
  }, [mode, refresh, setParams]);

  // Init: from ?po= or show picker
  useEffect(() => {
    const po = params.get("po");
    if (po) { startForPo(po); return; }
    api.get("/inventory/pos").then(({ data }) => {
      const eligible = (data.pos || []).filter((p) => ["approved", "location_planning", "ready_to_receive", "receiving"].includes(p.status));
      setPickerPOs(eligible);
    }).catch(() => setPickerPOs([]));
  }, []);

  // Live activity poll — every 3s all staff on the same PO see each other's scans.
  useEffect(() => {
    if (!session || result) return;
    let stop = false;
    const tick = async () => {
      try {
        const { data: f } = await api.get(`/inventory/receiving/session/${session.id}/feed`);
        if (stop) return;
        setData((prev) => {
          if (!prev) return prev;
          const lines = (prev.lines || []).map((l) => f.line_state?.[l.id] ? { ...l, received_qty: f.line_state[l.id].received_qty } : l);
          return { ...prev, lines, line_locations: f.line_locations || prev.line_locations, summary: f.summary || prev.summary, recent_events: f.recent_events || prev.recent_events };
        });
      } catch (err) { console.debug("receiving feed poll skipped", err?.message || err); }
    };
    const iv = setInterval(tick, 3000);
    return () => { stop = true; clearInterval(iv); };
  }, [session, result]);

  const showFlash = (type, text) => { setFlash({ type, text }); setTimeout(() => setFlash(null), 2200); };
  const triggerFx = (kind) => { setFx(kind); setTimeout(() => setFx((f) => (f === kind ? null : f)), 650); };

  const sendScan = async (barcode, locationCode, asUnexpected = false, sku = "", cond = condition) => {
    setBusy(true);
    try {
      const body = { session_id: session.id, barcode, quantity: mode === "bulk" ? Number(qty) || 1 : 1, mode, condition: cond, verify_skipped: skipVerify };
      if (locationCode) body.location_code = locationCode;
      if (asUnexpected) { body.unexpected = true; body.sku = sku; }
      const { data: r } = await api.post("/inventory/receiving/scan", body);
      if (r.status === "need_location") {
        setPendingBarcode(barcode);
        setRecommend(null);
        setAssignMode(true);
        setPendingLine(r.line || null);
        setLoc("");
        showFlash("warn", "No bin assigned — scan a bin to assign it");
        focusLoc();
        return;
      }
      if (r.status === "confirm_location") {
        // Known destination (pre-assigned or already in stock) — confirm by scanning the bin.
        setPendingBarcode(barcode);
        setAssignMode(false);
        setRecommend({ code: r.recommended_location, existing: r.existing_locations || [], product: r.product_title || r.sku });
        setPendingLine(r.line || null);
        setLoc("");
        showFlash("warn", r.message || `Put it in ${r.recommended_location}`);
        focusLoc();
        return;
      }
      if (r.status === "unexpected") { setUnexpected(barcode); return; }
      if (r.status === "ok") {
        setPendingBarcode(null);
        setRecommend(null);
        setAssignMode(false);
        setPendingLine(null);
        setMismatch(null);
        if (overrideLoc) { setOverrideLoc(""); setShowOverride(false); } // override is one-shot
        beep("ok"); triggerFx("ok");
        setLastPlacement({ location_code: r.location_code, sku: r.sku, product_title: r.product_title, quantity: r.quantity, condition: r.condition || cond, received_qty: r.line?.received_qty, expected_qty: r.line?.expected_qty, auto_placed: r.auto_placed });
        const cm = CONDITION_META[r.condition || cond];
        const tag = (r.condition && r.condition !== "good") ? ` [${cm?.l}]` : "";
        const headline = r.warning || (r.auto_placed ? `Put it in ${r.location_code} — +${r.quantity} ${r.product_title || r.sku}` : `+${r.quantity} ${r.product_title || r.sku} → ${r.location_code}`);
        showFlash(r.warning ? "warn" : (r.condition !== "good" ? "hold" : "ok"), `${headline}${tag}`);
        await refresh(session.id);
        setScan(""); focusScan();
        // Clear the big placement callout after ~3s and jump back to the product field.
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
        clearTimerRef.current = setTimeout(() => { setLastPlacement(null); focusScan(); }, 3000);
      }
    } catch (e) { toast.error(formatApiError(e)); setScan(""); focusScan(); }
    finally { setBusy(false); }
  };

  const onScanEnter = (raw) => {
    if (busy) return;
    const b = (raw !== undefined ? raw : scan).trim();
    if (!b) return;
    let locationCode = "";
    if (mode === "location") locationCode = data?.session?.active_location_code;
    else if (overrideLoc) locationCode = overrideLoc; // "put somewhere else" override
    if (mode === "location" && !locationCode) { showFlash("warn", "Set an active location first"); setTimeout(() => locRef.current?.focus(), 30); return; }
    sendScan(b, locationCode);
  };

  const onLocEnter = async (raw) => {
    let code = normBin(raw !== undefined ? raw : loc);
    if (!code && recommend) code = recommend.code; // empty Enter accepts the shown bin
    if (!code) return;
    if (mode === "location") {
      try {
        await api.post("/inventory/receiving/set-location", { session_id: session.id, location_code: code });
        await refresh(session.id);
        setLoc(""); showFlash("ok", `Active location: ${code}`); focusScan();
      } catch (e) { toast.error(formatApiError(e)); }
    } else if (pendingBarcode) {
      if (recommend) {
        const existingCodes = (recommend.existing || []).map((e) => e.location_code);
        if (existingCodes.includes(code)) {
          // Right bin — consolidate (sendScan plays ok chirp + green flash).
          setLoc("");
          await sendScan(pendingBarcode, code);
        } else {
          // Wrong bin — alert and require an explicit tap to add it as an extra location.
          beep("err"); triggerFx("err");
          setMismatch({ code, barcode: pendingBarcode });
          setLoc("");
        }
        return;
      }
      const b = pendingBarcode; setLoc("");
      await sendScan(b, code);
    }
  };

  const confirmMismatch = async () => {
    if (!mismatch) return;
    const { barcode, code } = mismatch;
    setMismatch(null);
    await sendScan(barcode, code); // place at the new/additional location
  };
  const cancelMismatch = () => { setMismatch(null); setTimeout(() => locRef.current?.focus(), 30); };

  const undo = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/inventory/receiving/undo", { session_id: session.id });
      showFlash("warn", `Undid ${r.quantity} × ${r.sku}`);
      await refresh(session.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); focusScan(); }
  };

  // Manually correct a line's received count (+/-) without needing a scan/undo.
  const adjustLine = async (lineId, delta, code) => {
    try {
      await api.post("/inventory/receiving/adjust", { session_id: session.id, po_line_id: lineId, delta, location_code: code || undefined });
      await refresh(session.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openBinEdit = (lineId, code) => { setEditBin({ lineId, from: code || "" }); setBinVal(code || ""); };
  const saveBin = async () => {
    if (!editBin) return;
    const to = normBin(binVal);
    if (!to || to === editBin.from) { setEditBin(null); return; }
    try {
      const { data } = await api.post("/inventory/receiving/reassign", { session_id: session.id, po_line_id: editBin.lineId, from_code: editBin.from, to_code: to });
      setEditBin(null);
      showFlash("ok", data.moved ? `Moved ${data.moved} → ${to}` : `Bin set to ${to}`);
      await refresh(session.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const complete = async () => {
    setCompleting(true);
    try {
      const { data: r } = await api.post("/inventory/receiving/complete", { session_id: session.id });
      setResult(r);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCompleting(false); }
  };

  const recordUnexpected = async () => {
    const code = loc.trim().toUpperCase() || data?.session?.active_location_code;
    if (!code) { toast.error("Enter a location for the unexpected item"); return; }
    await sendScan(unexpected, code, true, unexpected, condition);
    setUnexpected(null); setLoc("");
  };

  if (!isStaff) return null;

  // ---- Result screen ----
  if (result) {
    return (
      <div className="fixed inset-0 bg-bh-navy-deep text-white flex flex-col items-center justify-center p-8 text-center" data-testid="rcv-result">
        <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 ${result.has_discrepancy ? "bg-amber-500/20" : "bg-emerald-500/20"}`}>
          {result.has_discrepancy ? <AlertTriangle className="w-14 h-14 text-amber-400" /> : <CheckCircle2 className="w-14 h-14 text-emerald-400" />}
        </div>
        <h1 className="text-3xl font-extrabold mb-2 text-white" style={{ fontFamily: "Manrope" }}>Progress saved</h1>
        <p className="text-white/60 mb-8 max-w-md">This PO stays <span className="font-semibold text-white">open</span> — you can keep receiving against it any time (more boxes, a found item, another day). {result.has_discrepancy ? "Some lines don't match expected quantities yet." : "All received lines balanced so far."}</p>
        {result.duration_seconds != null && <p className="text-white/50 -mt-6 mb-8 text-sm flex items-center gap-1.5"><Clock className="w-4 h-4" /> Hands-on time: {formatDuration(result.duration_seconds)}</p>}
        <div className="flex flex-col sm:flex-row items-center gap-3">
          {data?.po?.account_id && (
            <button onClick={() => window.open(`${API_BASE}/inventory/export/shipstation?account_id=${data.po.account_id}&format=xlsx`, "_blank", "noopener")} data-testid="rcv-result-shipstation" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold text-lg px-8 py-4 rounded-2xl">
              <Boxes className="w-5 h-5" /> ShipStation sheet
            </button>
          )}
          <button onClick={() => navigate("/admin")} data-testid="rcv-result-done" className="inline-flex items-center gap-2 bg-bh-orange hover:bg-bh-orange-dark text-white font-bold text-lg px-8 py-4 rounded-2xl">Done</button>
        </div>
      </div>
    );
  }

  // ---- PO picker ----
  if (!session) {
    return (
      <div className="fixed inset-0 bg-bh-navy-deep text-white flex flex-col overflow-hidden" data-testid="rcv-picker">
        <header className="flex items-center justify-between px-6 py-4 bg-bh-navy/60 border-b border-white/10">
          <div className="flex items-center gap-4"><img src={LOGO} alt="BoxHero" className="h-8" /><div className="h-6 w-px bg-white/20" /><span className="flex items-center gap-2 text-lg font-bold" style={{ fontFamily: "Manrope" }}><Boxes className="w-6 h-6 text-bh-orange" /> Inventory Receiving</span></div>
          <button onClick={() => navigate("/admin")} data-testid="rcv-picker-exit" className="text-white/70 hover:text-white text-sm px-3 py-2 rounded-lg hover:bg-white/10 inline-flex items-center gap-2"><X className="w-5 h-5" /> Exit</button>
        </header>
        <div className="flex-1 overflow-y-auto max-w-3xl mx-auto w-full px-6 py-10">
          <h1 className="text-3xl font-extrabold mb-2 text-white" style={{ fontFamily: "Manrope" }}>Pick a PO to receive</h1>
          <p className="text-white/60 mb-8">Approved or in-progress inbound POs are ready to scan.</p>
          {pickerPOs === null ? <div className="flex items-center gap-2 text-white/50"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
            : pickerPOs.length === 0 ? <p className="text-white/50">No POs ready to receive. Approve a PO in the Inventory console first.</p>
            : <div className="space-y-2">
              {pickerPOs.map((p) => (
                <button key={p.id} onClick={() => startForPo(p.id)} data-testid={`rcv-pick-${p.id}`} className="w-full flex items-center justify-between bg-white/5 border border-white/10 hover:border-bh-orange hover:bg-white/10 rounded-2xl px-5 py-4 text-left">
                  <div><p className="text-lg font-bold font-mono">{p.boxhero_ref}</p><p className="text-xs text-white/50 mt-0.5">{p.company_name} · {p.line_count} lines · {p.expected_units} units · {PO_STATUS_META[p.status]?.label}</p></div>
                  <ChevronRight className="w-6 h-6 text-bh-orange" />
                </button>
              ))}
            </div>}
        </div>
      </div>
    );
  }

  const lines = data?.lines || [];
  const lineLocs = data?.line_locations || {};
  const summary = data?.summary || {};
  const tallies = summary.tallies || { good: 0, hold: 0 };
  const contributors = summary.contributors || [];
  const events = data?.recent_events || [];
  const totalExp = lines.reduce((s, l) => s + (l.expected_qty || 0), 0);
  const totalRcv = lines.reduce((s, l) => s + (l.received_qty || 0), 0);

  return (
    <div className="fixed inset-0 bg-bh-navy-deep text-white flex flex-col overflow-hidden" data-testid="rcv-station">
      <style>{`@keyframes bh-shake{10%,90%{transform:translateX(-3px)}20%,80%{transform:translateX(5px)}30%,50%,70%{transform:translateX(-9px)}40%,60%{transform:translateX(9px)}}.bh-shake{animation:bh-shake .5s ease}@keyframes bh-flash-green{0%{box-shadow:0 0 0 0 rgba(16,185,129,0)}30%{box-shadow:0 0 0 10px rgba(16,185,129,.65)}100%{box-shadow:0 0 0 0 rgba(16,185,129,0)}}.bh-flash-green{animation:bh-flash-green .7s ease}`}</style>
      <header className="flex items-center justify-between px-6 py-3 bg-bh-navy/60 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="BoxHero" className="h-7" />
          <div className="h-6 w-px bg-white/20" />
          <span className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}><Boxes className="w-5 h-5 text-bh-orange" /> {data?.po?.boxhero_ref}</span>
          <span className="text-xs text-white/40">{data?.po?.company_name}</span>
        </div>
        <div className="flex items-center gap-2">
          {data?.po?.account_id && (
            <button onClick={() => window.open(`${API_BASE}/inventory/export/shipstation?account_id=${data.po.account_id}&format=xlsx`, "_blank", "noopener")} data-testid="rcv-shipstation-sheet" className="text-white/70 hover:text-white text-sm px-3 py-2 rounded-lg hover:bg-white/10 inline-flex items-center gap-2"><Boxes className="w-5 h-5" /> ShipStation sheet</button>
          )}
          <button onClick={() => navigate("/admin")} data-testid="rcv-exit" className="text-white/70 hover:text-white text-sm px-3 py-2 rounded-lg hover:bg-white/10 inline-flex items-center gap-2"><X className="w-5 h-5" /> Exit</button>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Active scan zone — front and centre */}
        <div className="lg:flex-[2.4] min-w-0 border-b lg:border-b-0 lg:border-r border-white/10 p-6 flex flex-col overflow-y-auto" data-testid="rcv-active-zone">
          {/* Inputs: product (primary) + bin (to the right) */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 min-w-0">
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-1.5 font-bold">1 · Scan product</label>
              <div className="flex gap-2">
                <input ref={scanRef} autoFocus value={scan} inputMode={kbd ? "text" : "none"} onChange={(e) => { setScan(e.target.value); armAuto(autoTimerRef, e.target.value, onScanEnter); }} onKeyDown={(e) => { if (e.key === "Enter" || e.keyCode === 13) { e.preventDefault(); if (autoTimerRef.current) clearTimeout(autoTimerRef.current); onScanEnter(e.currentTarget.value); } }} data-testid="rcv-scan-input" placeholder="Scan barcode…" className="flex-1 min-w-0 text-3xl font-mono px-4 py-4 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-4 focus:ring-bh-orange/50" />
                <button onClick={() => { setKbd((k) => { const n = !k; localStorage.setItem("bh-rcv-kbd", n ? "1" : "0"); return n; }); setTimeout(() => scanRef.current?.focus(), 20); }} data-testid="rcv-keyboard-toggle" title="Toggle on-screen keyboard" className={`px-4 rounded-xl inline-flex items-center shrink-0 ${kbd ? "bg-white text-bh-navy-deep" : "bg-white/10 text-white/70 hover:bg-white/20"}`}><Keyboard className="w-6 h-6" /></button>
                <button onClick={() => { if (autoTimerRef.current) clearTimeout(autoTimerRef.current); onScanEnter(scanRef.current?.value); }} disabled={busy} data-testid="rcv-scan-btn" className="bg-bh-orange hover:bg-bh-orange-dark disabled:opacity-60 text-white font-bold px-5 rounded-xl inline-flex items-center shrink-0"><ScanLine className="w-6 h-6" /></button>
              </div>
            </div>
            <div className="sm:w-72 shrink-0">
              <label className={`block text-xs uppercase tracking-widest mb-1.5 font-bold ${pendingBarcode ? (assignMode ? "text-blue-400" : "text-bh-orange") : "text-white/30"}`}>2 · {pendingBarcode ? (assignMode ? "Scan bin to assign" : "Scan bin to confirm") : "Bin — automatic when the PO says where"}</label>
              <input ref={locRef} value={loc} disabled={!pendingBarcode} inputMode={kbd ? "text" : "none"} onChange={(e) => { setLoc(e.target.value); armAuto(locTimerRef, e.target.value, onLocEnter); }} onKeyDown={(e) => { if (e.key === "Enter" || e.keyCode === 13) { e.preventDefault(); if (locTimerRef.current) clearTimeout(locTimerRef.current); onLocEnter(e.currentTarget.value); } }} data-testid="rcv-loc-input" placeholder={pendingBarcode ? "Scan bin…" : "—"} className={`w-full text-2xl font-mono uppercase px-4 py-4 rounded-xl focus:outline-none focus:ring-4 focus:ring-bh-orange/50 ${pendingBarcode ? "bg-white text-slate-900" : "bg-white/5 text-white/30 border border-white/10 cursor-not-allowed"}`} />
            </div>
          </div>

          {/* Flash (over-receive / warnings) */}
          {flash && (
            <div data-testid="rcv-flash" className={`mt-3 rounded-xl px-4 py-2.5 font-semibold text-sm flex items-center gap-2 ${flash.type === "ok" ? "bg-emerald-500 text-white" : flash.type === "hold" ? "bg-red-500 text-white" : "bg-amber-500 text-white"}`}>
              {flash.type === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {flash.text}
            </div>
          )}

          {/* GIANT PUT-IT-HERE / ASSIGN / RECEIVED callout — the biggest thing on screen */}
          <div className="mt-5 flex-1 min-h-[280px] flex" data-testid="rcv-placement">
            {mismatch ? (
              <div className="w-full rounded-3xl bg-red-500 text-white px-8 py-8 shadow-2xl flex flex-col items-center justify-center text-center bh-shake" data-testid="rcv-mismatch">
                <p className="text-base font-extrabold uppercase tracking-widest flex items-center gap-2"><AlertTriangle className="w-6 h-6" /> Different bin scanned</p>
                <p className="text-6xl xl:text-7xl font-black font-mono leading-none mt-3 break-all">{mismatch.code}</p>
                <p className="text-lg font-semibold mt-3 max-w-xl">isn&apos;t where this product lives{recommend ? <> (it&apos;s at <span className="font-mono font-bold">{recommend.code}</span>)</> : ""}. Adding it stores this SKU in two bins.</p>
                <div className="flex flex-col sm:flex-row gap-3 mt-5">
                  <button onClick={confirmMismatch} data-testid="rcv-mismatch-confirm" className="inline-flex items-center justify-center gap-2 bg-white text-red-600 font-bold px-5 py-3 rounded-xl"><MapPin className="w-5 h-5" /> Add {mismatch.code} as extra location</button>
                  <button onClick={cancelMismatch} data-testid="rcv-mismatch-cancel" className="inline-flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 text-white font-semibold px-5 py-3 rounded-xl"><X className="w-5 h-5" /> Cancel</button>
                </div>
              </div>
            ) : recommend ? (
              <div className={`w-full rounded-3xl bg-amber-400 text-bh-navy-deep px-8 py-8 shadow-2xl flex flex-col items-center justify-center text-center animate-pulse ${fx === "err" ? "bh-shake" : ""}`} data-testid="rcv-recommend-active">
                <p className="text-base font-extrabold uppercase tracking-widest flex items-center gap-2"><MapPin className="w-6 h-6" /> Put it here</p>
                <p className="text-8xl xl:text-9xl font-black font-mono leading-none mt-4 break-all" data-testid="rcv-recommend-loc">{recommend.code}</p>
                <p className="text-2xl font-bold mt-4 max-w-full truncate">{recommend.product}</p>
                {pendingLine && pendingLine.expected_qty != null && (
                  <p className="text-5xl xl:text-6xl font-black mt-4 leading-none" data-testid="rcv-recommend-count">{pendingLine.received_qty || 0} <span className="opacity-60">of</span> {pendingLine.expected_qty} <span className="text-2xl xl:text-3xl font-extrabold opacity-70">· {Math.max(0, (pendingLine.expected_qty || 0) - (pendingLine.received_qty || 0))} left</span></p>
                )}
                {(() => {
                  const primary = (recommend.existing || []).find((e) => e.location_code === recommend.code);
                  const others = (recommend.existing || []).filter((e) => e.location_code !== recommend.code).map((e) => e.location_code);
                  return (
                    <>
                      {primary && primary.available_qty > 0 && <p className="text-sm font-semibold opacity-80 mt-1">{primary.available_qty} unit{primary.available_qty === 1 ? "" : "s"} already in this bin</p>}
                      {others.length > 0 && <p className="text-xs font-semibold opacity-70 mt-0.5" data-testid="rcv-recommend-others">Also stored in {others.join(", ")}</p>}
                    </>
                  );
                })()}
                <div className="mt-5 inline-flex items-center gap-2 bg-bh-navy-deep text-amber-300 font-bold px-5 py-2.5 rounded-full"><ScanLine className="w-5 h-5" /> Scan the bin barcode to confirm</div>
              </div>
            ) : assignMode ? (
              <div className="w-full rounded-3xl bg-blue-500 text-white px-8 py-8 shadow-2xl flex flex-col items-center justify-center text-center animate-pulse" data-testid="rcv-assign-active">
                <p className="text-base font-extrabold uppercase tracking-widest flex items-center gap-2"><MapPin className="w-6 h-6" /> No bin assigned yet</p>
                <p className="text-5xl xl:text-6xl font-black leading-tight mt-4">Scan a bin to assign it</p>
                <p className="text-xl font-semibold mt-3 opacity-90 truncate max-w-full font-mono">{pendingBarcode}</p>
                {pendingLine && pendingLine.expected_qty != null && (
                  <p className="text-5xl xl:text-6xl font-black mt-4 leading-none" data-testid="rcv-assign-count">{pendingLine.received_qty || 0} <span className="opacity-60">of</span> {pendingLine.expected_qty} <span className="text-2xl xl:text-3xl font-extrabold opacity-70">· {Math.max(0, (pendingLine.expected_qty || 0) - (pendingLine.received_qty || 0))} left</span></p>
                )}
                <div className="mt-5 inline-flex items-center gap-2 bg-bh-navy-deep text-blue-200 font-bold px-5 py-2.5 rounded-full"><ScanLine className="w-5 h-5" /> It&apos;s saved for this product for the rest of the PO</div>
              </div>
            ) : lastPlacement ? (
              <div className={`w-full rounded-3xl bg-emerald-500 text-white px-8 py-8 shadow-2xl flex flex-col items-center justify-center text-center ${fx === "ok" ? "bh-flash-green" : ""}`} data-testid="rcv-placement-active">
                <p className="text-base font-extrabold uppercase tracking-widest flex items-center gap-2"><CheckCircle2 className="w-6 h-6" /> {lastPlacement.auto_placed ? "Put it in" : "Received"}
                  {lastPlacement.condition && lastPlacement.condition !== "good" && (
                    <span className="text-xs font-extrabold uppercase bg-white/20 px-2.5 py-1 rounded-full ml-1">{CONDITION_META[lastPlacement.condition]?.l || "Held"}</span>
                  )}
                </p>
                <p className="text-8xl xl:text-9xl font-black font-mono leading-none mt-4 break-all" data-testid="rcv-placement-loc">{lastPlacement.location_code}</p>
                <p className="text-2xl font-bold mt-4 max-w-full truncate">+{lastPlacement.quantity} · {lastPlacement.product_title || lastPlacement.sku}</p>
                {lastPlacement.expected_qty != null && (
                  <p className="text-5xl xl:text-6xl font-black mt-4 leading-none" data-testid="rcv-placement-count">{lastPlacement.received_qty || 0} <span className="opacity-60">of</span> {lastPlacement.expected_qty} <span className="text-2xl xl:text-3xl font-extrabold opacity-70">· {Math.max(0, (lastPlacement.expected_qty || 0) - (lastPlacement.received_qty || 0))} left</span></p>
                )}
              </div>
            ) : (
              <div className="w-full rounded-3xl border-2 border-dashed border-white/15 flex flex-col items-center justify-center text-center py-16">
                <ScanLine className="w-12 h-12 mx-auto text-white/25" />
                <p className="text-white/40 text-lg font-semibold mt-4">Scan a product — where to put it shows here</p>
              </div>
            )}
          </div>

          {/* Secondary tools */}
          <div className="mt-5 flex flex-wrap items-center gap-2 pt-4 border-t border-white/10" data-testid="rcv-tools">
            <div className="flex gap-1.5" data-testid="rcv-modes">
              {MODES.map((m) => (
                <button key={m.v} onClick={() => setMode(m.v)} data-testid={`rcv-mode-${m.v}`} className={`rounded-lg px-3 py-2 text-xs font-bold transition-colors ${mode === m.v ? "bg-bh-orange text-white" : "bg-white/10 text-white/70 hover:bg-white/20"}`}>{m.l}</button>
              ))}
            </div>
            <div className="h-6 w-px bg-white/15" />
            <div className="flex gap-1.5" data-testid="rcv-conditions">
              {CONDITIONS.map((c) => (
                <button key={c.v} onClick={() => setCondition(c.v)} data-testid={`rcv-cond-${c.v}`} className={`rounded-lg px-3 py-2 text-xs font-bold border transition-colors ${condition === c.v ? `${c.cls} text-white border-transparent` : "bg-white/5 text-white/70 border-white/15 hover:bg-white/10"}`}>{c.l}</button>
              ))}
            </div>
            {mode === "bulk" && (
              <div className="flex items-center gap-2" data-testid="rcv-bulk-qty">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="rcv-qty-minus" className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                <input type="number" min="1" value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))} data-testid="rcv-qty" className="w-16 text-center text-lg font-extrabold px-1 py-1.5 rounded-lg bg-white text-slate-900" />
                <button onClick={() => setQty((q) => q + 1)} data-testid="rcv-qty-plus" className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
            )}
            {!showOverride && !overrideLoc ? (
              <button onClick={() => setShowOverride(true)} data-testid="rcv-override-toggle" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5"><Shuffle className="w-3.5 h-3.5" /> Override bin</button>
            ) : (
              <div className="flex items-center gap-1.5">
                <input value={overrideLoc} onChange={(e) => setOverrideLoc(e.target.value.toUpperCase())} data-testid="rcv-override-input" placeholder="Override bin (once)" className="w-32 text-sm font-mono uppercase px-2.5 py-2 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-bh-orange/40" />
                <button onClick={() => { setOverrideLoc(""); setShowOverride(false); }} data-testid="rcv-override-clear" className="px-2 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white"><X className="w-4 h-4" /></button>
              </div>
            )}
            <button onClick={() => { setSkipVerify((v) => { const n = !v; localStorage.setItem("bh-rcv-skipverify", n ? "1" : "0"); return n; }); }} data-testid="rcv-skip-verify" title="Skip the confirm-bin scan for known bins" className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold border transition-colors ${skipVerify ? "bg-amber-500 text-white border-transparent" : "bg-white/5 text-white/70 border-white/15 hover:bg-white/10"}`}>
              {skipVerify ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />} Skip bin check
            </button>
            <div className="flex-1" />
            {data?.po && (
              <POPhotos accountId={data.po.account_id} poId={data.po.id} kind="receiving" compact dark
                photos={data.po.photos || []}
                onChange={(ph) => setData((d) => ({ ...d, po: { ...d.po, photos: ph } }))} />
            )}
            <button onClick={undo} disabled={busy} data-testid="rcv-undo" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2.5 rounded-lg"><Undo2 className="w-4 h-4" /> Undo</button>
            <button onClick={complete} disabled={completing} data-testid="rcv-complete" className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white text-sm font-bold px-4 py-2.5 rounded-lg">{completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />} Done for now</button>
          </div>
        </div>

        {/* Right: progress (shrunken sidebar) */}
        <div className="lg:w-[300px] xl:w-[340px] shrink-0 overflow-y-auto p-5" data-testid="rcv-progress">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-bold uppercase tracking-widest text-white/50" style={{ fontFamily: "Manrope" }}>Progress</h2>
            <span className="text-sm text-white/70 font-semibold tabular-nums">{totalRcv} / {totalExp}</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full mb-5 overflow-hidden"><div className="h-full bg-bh-orange transition-all" style={{ width: `${totalExp ? Math.min(100, (totalRcv / totalExp) * 100) : 0}%` }} /></div>

          {/* Now-receiving spotlight — current item's received/expected pinned up top */}
          {(() => {
            const cur = pendingLine || (lastPlacement && lastPlacement.expected_qty != null ? { product_title: lastPlacement.product_title, sku: lastPlacement.sku, received_qty: lastPlacement.received_qty, expected_qty: lastPlacement.expected_qty } : null);
            if (!cur || cur.expected_qty == null) return null;
            const rq = cur.received_qty || 0, eq = cur.expected_qty || 0;
            return (
              <div className="mb-5 rounded-2xl bg-bh-orange/15 border border-bh-orange/40 px-4 py-3" data-testid="rcv-now-receiving">
                <p className="text-[10px] uppercase tracking-widest text-bh-orange font-bold mb-1">Now receiving</p>
                <div className="flex items-end justify-between gap-2">
                  <p className="text-sm font-semibold text-white truncate min-w-0">{cur.product_title || cur.sku}</p>
                  <span className="text-2xl font-black tabular-nums text-white shrink-0" data-testid="rcv-now-count">{rq}<span className="text-white/50 text-lg"> / {eq}</span></span>
                </div>
                <p className="text-xs font-semibold text-bh-orange mt-0.5">{Math.max(0, eq - rq)} left to receive</p>
              </div>
            );
          })()}


          <div className="grid grid-cols-3 gap-2 mb-5" data-testid="rcv-tallies">
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-3 py-2.5"><p className="text-[10px] uppercase tracking-wider text-emerald-300/80">Good</p><p className="text-2xl font-extrabold text-emerald-300 tabular-nums" data-testid="rcv-tally-good">{tallies.good || 0}</p></div>
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 px-3 py-2.5"><p className="text-[10px] uppercase tracking-wider text-red-300/80">Held</p><p className="text-2xl font-extrabold text-red-300 tabular-nums" data-testid="rcv-tally-hold">{tallies.hold || 0}</p></div>
            <div className="rounded-xl bg-white/5 border border-white/15 px-3 py-2.5"><p className="text-[10px] uppercase tracking-wider text-white/50 flex items-center gap-1"><Clock className="w-3 h-3" /> Hands-on</p><p className="text-2xl font-extrabold text-white tabular-nums" data-testid="rcv-duration">{formatDuration(summary.duration_seconds)}</p></div>
          </div>

          {/* Team contributions */}
          {contributors.length > 0 && (
            <div className="mb-5" data-testid="rcv-contributors">
              <p className="text-xs uppercase tracking-wider text-white/40 mb-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Team ({contributors.length})</p>
              <div className="flex flex-wrap gap-1.5">
                {contributors.map((c) => (
                  <span key={c.user} data-testid={`rcv-contrib-${c.user}`} className="text-xs font-semibold bg-white/10 px-2.5 py-1 rounded-full">{c.user?.split("@")[0]} · {c.units}</span>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2" data-testid="rcv-lines">
            {(() => {
              const curSku = pendingLine?.sku || lastPlacement?.sku;
              const ordered = curSku ? [...lines].sort((a, b) => (b.sku === curSku ? 1 : 0) - (a.sku === curSku ? 1 : 0)) : lines;
              return ordered.map((l) => {
              const rq = l.received_qty || 0, eq = l.expected_qty || 0;
              const state = rq === eq ? "done" : rq > eq ? "over" : rq > 0 ? "partial" : "none";
              const cls = { done: "border-emerald-500/50 bg-emerald-500/5", over: "border-amber-500/60 bg-amber-500/10", partial: "border-blue-500/40 bg-blue-500/5", none: "border-white/10 bg-white/5" }[state];
              const locs = lineLocs[l.id] || {};
              const isCur = l.sku === curSku;
              return (
                <div key={l.id} data-testid={`rcv-line-${l.id}`} className={`rounded-xl border px-4 py-3 transition-all ${cls} ${isCur ? "ring-2 ring-bh-orange shadow-lg" : ""}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{l.product_title || l.sku || "Item"}</p>
                      <p className="text-xs text-white/40 font-mono">{l.sku} · {l.barcode}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => adjustLine(l.id, -1, Object.keys(locs)[0])} disabled={rq <= 0} data-testid={`rcv-line-minus-${l.id}`} title="Remove one (over-scan fix)" className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-25 flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                      <span className={`text-lg font-extrabold tabular-nums w-12 text-center ${state === "over" ? "text-amber-300" : state === "done" ? "text-emerald-300" : "text-white"}`}>{rq}/{eq}</span>
                      <button onClick={() => adjustLine(l.id, 1, Object.keys(locs)[0])} data-testid={`rcv-line-plus-${l.id}`} title="Add one" className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`rcv-line-bins-${l.id}`}>
                    {editBin && editBin.lineId === l.id ? (
                      <span className="inline-flex items-center gap-1">
                        <input autoFocus value={binVal} onChange={(e) => setBinVal(e.target.value.toUpperCase())}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveBin(); } else if (e.key === "Escape") setEditBin(null); }}
                          data-testid={`rcv-line-bin-input-${l.id}`} placeholder="bin" className="w-20 text-[11px] font-mono uppercase px-2 py-1 rounded bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-bh-orange/50" />
                        <button onClick={saveBin} data-testid={`rcv-line-bin-save-${l.id}`} className="text-emerald-300 hover:text-emerald-200"><CheckCircle2 className="w-4 h-4" /></button>
                        <button onClick={() => setEditBin(null)} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
                      </span>
                    ) : Object.keys(locs).length > 0 ? (
                      Object.entries(locs).map(([code]) => (
                        <button key={code} onClick={() => openBinEdit(l.id, code)} data-testid={`rcv-line-bin-${l.id}`} title="Click to change / move bin"
                          className="text-[11px] font-semibold bg-white/10 hover:bg-white/20 px-2 py-0.5 rounded-full font-mono inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{code}</button>
                      ))
                    ) : (
                      <button onClick={() => openBinEdit(l.id, "")} data-testid={`rcv-line-bin-add-${l.id}`} className="text-[11px] font-semibold text-white/50 hover:text-white bg-white/5 hover:bg-white/15 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> set bin</button>
                    )}
                  </div>
                </div>
              );
            });
            })()}
          </div>

          {/* Live activity feed */}
          <div className="mt-6" data-testid="rcv-activity">
            <p className="text-xs uppercase tracking-wider text-white/40 mb-2 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Live activity</p>
            {events.length === 0 ? <p className="text-xs text-white/30">No scans yet.</p> : (
              <div className="space-y-1.5">
                {events.slice(0, 15).map((e) => {
                  const cm = CONDITION_META[e.condition] || CONDITION_META.good;
                  const isRev = e.event_type === "reverse_receipt";
                  const isUnx = e.event_type === "unexpected_receipt";
                  return (
                    <div key={e.id} data-testid={`rcv-event-${e.id}`} className="flex items-center justify-between gap-2 text-xs bg-white/5 rounded-lg px-3 py-1.5">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isRev ? "bg-slate-400" : isUnx ? "bg-amber-400" : cm.cls}`} />
                        <span className="font-mono truncate">{e.quantity > 0 ? "+" : ""}{e.quantity} {e.sku || e.barcode || "?"}</span>
                        {isUnx && <span className="text-[10px] text-amber-300 font-bold shrink-0">UNEXPECTED</span>}
                        {isRev && <span className="text-[10px] text-slate-400 font-bold shrink-0">UNDO</span>}
                        {e.condition && e.condition !== "good" && !isRev && <span className={`text-[10px] font-bold shrink-0 ${cm.text}`}>{cm.l.toUpperCase()}</span>}
                        <span className="text-white/40 font-mono shrink-0">→ {e.to_location || e.location_code || "—"}</span>
                      </span>
                      <span className="text-white/40 shrink-0">{(e.user || "").split("@")[0]}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Unexpected modal */}
      {unexpected && (
        <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-6" data-testid="rcv-unexpected-modal">
          <div className="bg-white text-slate-900 rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-6 h-6 text-amber-500" /><h3 className="text-lg font-bold">Not on this PO</h3></div>
            <p className="text-sm text-slate-600 mb-4">Barcode <span className="font-mono font-semibold">{unexpected}</span> isn&apos;t on this PO. Record it as an unexpected receipt to a location, or skip it.</p>
            <input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Location (e.g. STAGING)" data-testid="rcv-unexpected-loc" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm uppercase font-mono mb-4" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setUnexpected(null); setLoc(""); focusScan(); }} data-testid="rcv-unexpected-skip" className="px-4 py-2 text-sm font-semibold text-slate-500">Skip</button>
              <button onClick={recordUnexpected} data-testid="rcv-unexpected-record" className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold rounded-md">Record unexpected</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
